/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { AsyncSubject, Observable, of } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { DBHandler } from './db-handler';
import { DbCacheStatus, DbCacheSyncObservers, DbCacheSyncObserverFunction } from './db.models';

export class DbCache {
    [k: string]: any;
    private status_: DbCacheStatus;
    private syncObservers: DbCacheSyncObservers;
    private wait$: AsyncSubject<DbCacheStatus>;

    constructor(
        private dbHandler: DBHandler,
        private storeName: string,
        private key: string,
        private userId?: string
    ) {
        this.status_ = DbCacheStatus.Loading;
        this.syncObservers = new Set();
        /* Wait for first sync */
        this.wait$ = new AsyncSubject();
    }

    get status(): DbCacheStatus {
        return this.status_;
    }

    private updateStatus(status: DbCacheStatus) {
        const oldStatus = this.status_;
        this.status_ = status;
        if (this.isFirstSyncComplete(oldStatus)) {
            this.wait$.next(this.status);
            this.wait$.complete();
        }
    }

    private isFirstSyncComplete(oldStatus: DbCacheStatus): boolean {
        return !this.wait$.closed && oldStatus === DbCacheStatus.Loading && this.status_ !== DbCacheStatus.Loading;
    }

    /**
     * Persists the local object to disk.
     * Remember to .subscribe() !
     */
    persist_(): Observable<any> {
        this.updateStatus(DbCacheStatus.Dirty);
        const store = this.getStore(this.userId, this.storeName);

        // Filter out member properties
        const filteredValue = (({
            status,
            status_,
            persist_,
            sync_,
            observe_,
            unobserve_,
            wait_,
            dbHandler,
            updateStatus,
            isFirstSyncComplete,
            getStore,
            syncObservers,
            wait$,
            ...rest
        }) => rest)(this);
        return store.setItem(this.key, filteredValue).pipe(
            tap(() => this.updateStatus(DbCacheStatus.Saved)),
            catchError(() => {
                this.logError(`Error persisting the key ${this.key} to db`);
                return of(filteredValue);
            })
        );
    }

    private getStore(userId: string, storeName: string) {
        return userId ? this.dbHandler.getUserStore(userId, storeName) : this.dbHandler.getGlobalStore(storeName);
    }

    /**
     * Reads the database value and stores it in cache
     * Remember to .subscribe() !
     */
    sync_(): Observable<any> {
        const store = this.getStore(this.userId, this.storeName);
        return store.getItem(this.key).pipe(
            map((item: any) => {
                item = item || {};
                if (!(item instanceof Object)) {
                    throw new Error('Tried to cache plain old data. Only Object types supported');
                }
                for (const key of Object.keys(item)) {
                    this[key] = item[key];
                }
                this.updateStatus(DbCacheStatus.Saved);
                this.syncObservers.forEach(syncObserver => {
                    syncObserver();
                });
                return item;
            })
        );
    }

    /**
     * Waits for the first time data is loaded.
     * Remember to .subscribe() !
     */
    wait_(): Observable<DbCacheStatus> {
        if (this.wait$.closed) {
            this.wait$ = new AsyncSubject();
            if (this.status_ !== DbCacheStatus.Loading) {
                this.wait$.next(this.status);
                this.wait$.complete();
            }
        }
        return this.wait$.asObservable();
    }

    observe_(syncObserverFunc: DbCacheSyncObserverFunction): void {
        this.syncObservers.add(syncObserverFunc);
        if (this.status_ !== DbCacheStatus.Loading) {
            syncObserverFunc();
        }
    }

    unobserve_(syncObserverFunc: DbCacheSyncObserverFunction): void {
        this.syncObservers.delete(syncObserverFunc);
    }

    private logError(...args: any[]): void {
        console.error('DbHandler', this.storeName, JSON.stringify(args));
    }
}
